import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Mail, Phone, Building2, ArrowUpRight, Calendar } from "lucide-react";
import PublicLayout from "../../components/public/PublicLayout";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Demo() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const initialPlan = params.get("plan") || "";

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    firm_name: "",
    arn_number: "",
    city: "",
    approx_aum: "",
    num_clients: "",
    plan_interest: initialPlan,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.mobile) {
      toast.error("Name, email and mobile are required");
      return;
    }
    setSubmitting(true);
    try {
      try {
        await axios.post(`${API}/api/leads`, {
          ...form, source: "demo_request",
          requested_at: new Date().toISOString(),
        }, { timeout: 10000 });
      } catch (err) {
        const pending = JSON.parse(localStorage.getItem("__pending_demo_requests") || "[]");
        pending.push({ ...form, requested_at: new Date().toISOString() });
        localStorage.setItem("__pending_demo_requests", JSON.stringify(pending));
      }
      setDone(true);
      toast.success("Thanks — we'll be in touch within 1 business day.");
    } finally {
      setSubmitting(false);
    }
  };

  // Styled-on-dark input class
  const inp = "w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C8A96B]/60 focus:bg-white/[0.05] transition";

  if (done) {
    return (
      <PublicLayout title="Demo requested">
        <section className="max-w-xl mx-auto px-6 sm:px-10 py-24 text-center" data-testid="demo-thanks">
          <CheckCircle2 size={56} className="mx-auto text-[#C8A96B] mb-5" />
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight">Thanks, <span className="italic text-[#C8A96B]">{form.full_name.split(" ")[0]}</span>.</h1>
          <p className="text-white/60 mt-5 leading-[1.7]">
            We&apos;ve received your demo request. A team member will reach out at <span className="text-white">{form.email}</span> within 1 business day to schedule your 30-minute walkthrough.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/features" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-white/15 text-white/85 hover:bg-white/[0.04] transition">See features</Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition">View pricing</Link>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout title="Book a demo">
      <div data-testid="demo-page" className="px-6 sm:px-10 py-14">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-start">

          {/* Left column — pitch */}
          <div>
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40 mb-5">
              <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
              30-minute private walkthrough
            </div>
            <h1 className="text-4xl sm:text-5xl font-light tracking-[-0.02em] leading-[1.1]">
              See Kinntegra running on
              <br /><span className="italic text-[#C8A96B]">your own data.</span>
            </h1>
            <p className="text-white/55 mt-6 leading-[1.7]">
              Book a 30-min Zoom with a Kinntegra specialist. We&apos;ll spin up a sandbox with your Karvy/CAMS sample feed and walk you through the PAN-grouped dashboards, brokerage reconciliation and family AUM rollups — live.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                "Bring up to 2 colleagues — no extra cost",
                "You decide what to focus on — we tailor the demo",
                "Free 14-day trial offered at the end if you want",
                "Your sample data is wiped after the demo — guaranteed",
              ].map((line, i) => (
                <li key={i} className="flex gap-2.5 text-white/75">
                  <CheckCircle2 size={16} className="text-[#C8A96B] shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 inline-flex items-center gap-2 text-sm text-[#C8A96B] border border-[#C8A96B]/30 bg-[#C8A96B]/[0.05] rounded-full px-4 py-1.5">
              <Calendar size={14} /> Demos run Mon–Fri, 10 AM – 7 PM IST
            </div>
          </div>

          {/* Right column — form */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-6 sm:p-8">
            <h2 className="text-lg font-medium text-white flex items-center gap-2 mb-1">
              <Building2 size={18} className="text-[#C8A96B]" /> Tell us about your firm
            </h2>
            <p className="text-xs text-white/40 mb-5">We&apos;ll reach out within 1 business day.</p>
            <form onSubmit={submit} className="space-y-3.5">
              <div>
                <label className="text-xs font-medium text-white/55 block mb-1">Full name *</label>
                <input className={inp} value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} required data-testid="demo-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-white/55 flex items-center gap-1 mb-1"><Mail size={11} /> Email *</label>
                  <input type="email" className={inp} value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="demo-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-white/55 flex items-center gap-1 mb-1"><Phone size={11} /> Mobile *</label>
                  <input className={inp} value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} required data-testid="demo-mobile" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-white/55 block mb-1">Firm name</label>
                <input className={inp} value={form.firm_name} onChange={e => setForm({...form, firm_name: e.target.value})} data-testid="demo-firm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-white/55 block mb-1">ARN number</label>
                  <input className={inp} value={form.arn_number} onChange={e => setForm({...form, arn_number: e.target.value})} placeholder="ARN-160568" />
                </div>
                <div>
                  <label className="text-xs font-medium text-white/55 block mb-1">City</label>
                  <input className={inp} value={form.city} onChange={e => setForm({...form, city: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-white/55 block mb-1">Approx AUM</label>
                  <input className={inp} value={form.approx_aum} onChange={e => setForm({...form, approx_aum: e.target.value})} placeholder="₹50 Cr" />
                </div>
                <div>
                  <label className="text-xs font-medium text-white/55 block mb-1"># Clients</label>
                  <input type="number" className={inp} value={form.num_clients} onChange={e => setForm({...form, num_clients: e.target.value})} placeholder="250" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-white/55 block mb-1">Plan you&apos;re considering</label>
                <select
                  value={form.plan_interest}
                  onChange={e => setForm({...form, plan_interest: e.target.value})}
                  className={inp}
                  data-testid="demo-plan-interest"
                >
                  <option value="" className="bg-[#07111F]">— Choose a plan (optional) —</option>
                  <option value="starter" className="bg-[#07111F]">Starter — ₹4,999/mo</option>
                  <option value="pro" className="bg-[#07111F]">Pro — ₹8,999/mo</option>
                  <option value="enterprise" className="bg-[#07111F]">Enterprise — ₹16,999/mo</option>
                  <option value="exploring" className="bg-[#07111F]">Just exploring</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-white/55 block mb-1">What would you like us to focus on?</label>
                <textarea
                  value={form.message}
                  onChange={e => setForm({...form, message: e.target.value})}
                  rows={3}
                  className={inp + " resize-none"}
                  placeholder="Brokerage reconciliation, family AUM, tax planning, custom reports…"
                />
              </div>
              <button type="submit" disabled={submitting} className="w-full mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition disabled:opacity-60 disabled:cursor-not-allowed" data-testid="demo-submit">
                {submitting ? "Submitting…" : <>Book my demo <ArrowUpRight size={14} /></>}
              </button>
              <p className="text-[10px] text-white/35 text-center pt-1">By submitting, you agree to Kinntegra contacting you for a one-time demo. No spam, ever.</p>
            </form>
          </div>

        </div>
      </div>
    </PublicLayout>
  );
}
